// hooks/useAdminActivityTracker.js
import { useEffect, useRef } from 'react';
import TokenService from '../api/tokenService';

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

const useAdminActivityTracker = () => {
  const intervalRef = useRef(null);

  useEffect(() => {
    // Validate session function
    const validateSession = () => {
      // Update activity timestamp for valid sessions
      if (TokenService.hasToken() && !TokenService.isTokenExpired()) {
        TokenService.updateActivity();
      }
      
      // Token expired or session inactive
      if (TokenService.isTokenExpired() || TokenService.isInactive(SESSION_TIMEOUT)) {
        console.log('Session expired or inactive');
        TokenService.removeToken();
        window.location.href = '/login';
        return false;
      }
      
      return true;
    };

    // Activity tracking setup
    const setupActivityTracking = () => {
      const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      
      const activityHandler = () => {
        if (TokenService.hasToken() && !TokenService.isTokenExpired()) {
          TokenService.updateActivity();
        }
      };
      
      // Add event listeners
      activityEvents.forEach(event => {
        document.addEventListener(event, activityHandler, { passive: true });
      });

      // Return cleanup function for activity listeners
      return () => {
        activityEvents.forEach(event => {
          document.removeEventListener(event, activityHandler);
        });
      };
    };

    // Start session checking
    const startSessionChecker = () => {
      const CHECK_INTERVAL = 60 * 1000; // Check every minute
      
      const sessionChecker = () => {
        if (!validateSession()) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      };
      
      intervalRef.current = setInterval(sessionChecker, CHECK_INTERVAL);
    };

    // Initialize tracking only if user is authenticated
    if (TokenService.hasToken() && !TokenService.isTokenExpired()) {
      const cleanupActivityTracking = setupActivityTracking();
      startSessionChecker();
      
      // Cleanup function
      return () => {
        cleanupActivityTracking();
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);
};

export default useAdminActivityTracker;